import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Zoom,
  alpha,
  useTheme,
} from '@mui/material'
import { ArrowBack, Launch, Star, CheckCircle, Build } from '@mui/icons-material'
import Layout from '../components/Layout'
import { referenzen } from '../data/referenzen'
import { useTranslation } from 'react-i18next'
import { useColorMode } from '../theme'

const ReferenzDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const { mode } = useColorMode()
  const isDark = mode === 'dark'

  const ref = React.useMemo(() => referenzen.find((r) => r.slug === slug), [slug])

  React.useEffect(() => {
    if (!ref) {
      navigate('/referenzen')
    }
  }, [navigate, ref])

  if (!ref) {
    return null
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: { xs: '300px', md: '250px' },
            background: isDark
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            zIndex: 0,
          },
        }}
      >
        <Container
          maxWidth='lg'
          sx={{ py: { xs: 6, md: 8 }, position: 'relative', zIndex: 1 }}
        >
          <Fade in timeout={600}>
            <Box sx={{ mb: 4 }}>
              <Button
                onClick={() => navigate('/referenzen')}
                startIcon={<ArrowBack />}
                sx={{
                  color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  fontWeight: 600,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {t('referenceBack')}
              </Button>
            </Box>
          </Fade>

          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
              <Chip
                icon={<Star />}
                label={ref.industry}
                sx={{
                  mb: 4,
                  bgcolor: isDark
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: '1rem',
                  px: 3,
                  py: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              />
              <Typography
                variant='h2'
                component='h1'
                fontWeight={900}
                sx={{
                  mb: 4,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  lineHeight: 1.2,
                  px: { xs: 1, sm: 0 },
                }}
              >
                {ref.name}
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                  maxWidth: 720,
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                {ref.summary}
              </Typography>
            </Box>
          </Fade>

          <Stack spacing={4}>
            <Zoom in timeout={900}>
              <Card
                sx={{
                  p: { xs: 3, sm: 4 },
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha('#1f2937', 0.9)} 0%, ${alpha('#111827', 0.95)} 100%)`
                    : `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 255, 0.95) 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                  borderRadius: 4,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                }}
              >
                <Typography variant='h5' fontWeight={700} sx={{ mb: 2 }}>
                  {t('referenceHighlights')}
                </Typography>
                <List dense>
                  {ref.highlights.map((highlight) => (
                    <ListItem key={highlight} sx={{ alignItems: 'flex-start', py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircle sx={{ color: theme.palette.secondary.main }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant='body1'
                            sx={{
                              color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                              lineHeight: 1.6,
                            }}
                          >
                            {highlight}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Zoom>

            <Fade in timeout={1000}>
              <Card
                sx={{
                  p: { xs: 3, sm: 4 },
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha('#2d2d2d', 0.85)} 0%, ${alpha('#3d3d3d', 0.88)} 100%)`
                    : `linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(243, 244, 255, 0.95) 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  borderRadius: 4,
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 2, sm: 3 }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Build sx={{ color: theme.palette.secondary.main, fontSize: 32 }} />
                  <Box>
                    <Typography variant='h5' fontWeight={700} sx={{ mb: 2 }}>
                      {t('referenceTech')}
                    </Typography>
                    <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      {ref.tech.map((tech) => (
                        <Chip
                          key={tech}
                          label={tech}
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.secondary.main, 0.12),
                            color: theme.palette.secondary.main,
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Fade>

            <Fade in timeout={1100}>
              <Box sx={{ textAlign: 'center', pt: 2 }}>
                <Button
                  variant='contained'
                  endIcon={<Launch />}
                  href={ref.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{ minWidth: 240 }}
                >
                  {t('referenceVisitProject')}
                </Button>
              </Box>
            </Fade>
          </Stack>
        </Container>
      </Box>
    </Layout>
  )
}

export default ReferenzDetail
